// MIDI-Editor (Piano-Roll). Öffnet sich per Doppelklick auf einen MIDI-Clip in der Session.
// Noten setzen, verschieben, kürzen und verlängern, kopieren, löschen, Anschlag in der Velocity-Spur,
// Raster und Quantisieren. Jede Änderung ist ein Schritt für Rückgängig und geht live an den Raum.
import { useEffect, useRef, useState } from 'react';
import { barToBeat, beatAt, beatToBar, meterAt, secAt, spbAt } from '@srs/shared';
import { parseKey, type Note, type Project } from '@srs/shared';
import { useStudio } from '../state/store.ts';
import { engine } from '../audio/engine.ts';
import { Panel, toast } from '../components/ui.tsx';

const KEYS = 58, RULER = 24, VEL = 74;
const NAMES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
const BLACK = [1, 3, 6, 8, 10];
/** Notenname wie in Logic und Ableton (60 = C3) */
export const noteName = (m: number) => `${NAMES[m % 12]}${Math.floor(m / 12) - 2}`;

const GRIDS: { label: string; beats: number }[] = [
  { label: '1/4', beats: 1 }, { label: '1/8', beats: 0.5 }, { label: '1/16', beats: 0.25 }, { label: '1/32', beats: 0.125 },
  { label: '1/8 T', beats: 1 / 3 }, { label: '1/16 T', beats: 1 / 6 }, { label: 'Aus', beats: 0 },
];
const LENGTHS: { label: string; beats: number }[] = [
  { label: '1/1', beats: 4 }, { label: '1/2', beats: 2 }, { label: '1/4', beats: 1 }, { label: '1/8', beats: 0.5 }, { label: '1/16', beats: 0.25 }, { label: '1/32', beats: 0.125 },
];
const SOUNDS: { label: string; plugin: string; preset?: string }[] = [
  { label: 'Keys', plugin: 'srs:synth', preset: 'keys' }, { label: 'Bass', plugin: 'srs:synth', preset: 'bass' },
  { label: 'Lead', plugin: 'srs:synth', preset: 'lead' }, { label: 'Pad', plugin: 'srs:synth', preset: 'pad' },
  { label: 'Drums', plugin: 'srs:drum' },
];

type Drag =
  | { kind: 'move'; idx: number[]; anchor: number; x0: number; y0: number; dx: number; dy: number; copy: boolean; lastPitch: number }
  | { kind: 'resize'; idx: number[]; anchor: number; x0: number; dx: number }
  | { kind: 'draw'; t: number; b0: number; m: number; len: number; x0: number; dx: number }
  | { kind: 'band'; x0: number; y0: number; x1: number; y1: number; add: number[] }
  | { kind: 'vel'; idx: number[]; anchor: number; base: number[]; v: number }
  | { kind: 'end'; x0: number; dx: number; beats0: number }
  | null;

const r3 = (x: number) => Math.round(x * 1000) / 1000;
/** Der MIDI-Editor hat die Tastatur, solange er zuletzt angeklickt wurde (dann keine Timeline-Kürzel) */
export const pianoFocus = { active: false };

export function PianoRoll({ clipId, onClose }: { clipId: string; onClose: () => void }) {
  const clip = useStudio((s) => s.project.clips.find((c) => c.id === clipId));
  const track = useStudio((s) => s.project.tracks.find((t) => t.id === clip?.trackId));
  const commit = useStudio((s) => s.commit);
  const user = useStudio((s) => s.user);
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const view = useRef({ b0: 0, top: 84, ppb: 60, rowH: 14, init: false });
  const drag = useRef<Drag>(null);
  const sel = useRef<Set<number>>(new Set());
  const active = useRef(true);
  const hover = useRef<number | null>(null);
  const [grid, setGrid] = useState(0.25);
  const [len, setLen] = useState(1);
  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  // Clip gelöscht (auch von jemand anderem im Raum): Editor schließen
  useEffect(() => { if (!clip) onClose(); }, [clip, onClose]);

  const P = () => useStudio.getState().project;
  const cur = () => P().clips.find((c) => c.id === clipId);
  const notes = () => cur()?.notes ?? [];
  /** Viertel-Position des Clip-Anfangs im Song (Tempo-Spur) */
  const cb0 = () => { const c = cur(); return c ? beatAt(P(), c.start) : 0; };
  const clipBeats = () => { const c = cur(); return c ? beatAt(P(), c.start + c.duration) - beatAt(P(), c.start) : 0; };
  const snap = (b: number, g = grid) => (g ? Math.round(b / g) * g : b);
  const floorSnap = (b: number) => (grid ? Math.floor(b / grid + 1e-9) * grid : b);

  // Koordinaten
  const X = (b: number) => KEYS + (b - view.current.b0) * view.current.ppb;
  const B = (x: number) => view.current.b0 + (x - KEYS) / view.current.ppb;
  const Y = (m: number) => RULER + (view.current.top - m) * view.current.rowH;
  const M = (y: number) => view.current.top - Math.floor((y - RULER) / view.current.rowH);

  /** Vorschau der Noten mit laufender Mausaktion */
  function shown(): { n: Note; i: number; ghost?: boolean }[] {
    const ns = notes(), d = drag.current, out = ns.map((n, i) => ({ n, i }));
    if (!d) return out;
    if (d.kind === 'move') {
      const { dt, dm } = moveDelta(d);
      const moved = d.idx.map((i) => ({ n: { ...ns[i], t: Math.max(0, ns[i].t + dt), m: clampM(ns[i].m + dm) }, i }));
      if (d.copy) return [...out.map((o) => ({ ...o, ghost: false })), ...moved.map((o) => ({ ...o, i: -1 }))];
      return out.map((o) => moved.find((x) => x.i === o.i) ?? o);
    }
    if (d.kind === 'resize') {
      const dd = resizeDelta(d);
      return out.map((o) => d.idx.includes(o.i) ? { ...o, n: { ...o.n, d: Math.max(minLen(), o.n.d + dd) } } : o);
    }
    if (d.kind === 'draw') return [...out, { n: { t: d.t, d: drawLen(d), m: d.m, v: 0.8, by: { userId: user.id, at: '' } }, i: -1 }];
    if (d.kind === 'vel') return out.map((o) => { const k = d.idx.indexOf(o.i); return k >= 0 ? { ...o, n: { ...o.n, v: velFor(d, k) } } : o; });
    return out;
  }
  const minLen = () => grid || 1 / 16;
  const clampM = (m: number) => Math.max(0, Math.min(127, m));
  function moveDelta(d: Extract<Drag, { kind: 'move' }>) {
    const a = notes()[d.anchor];
    const dt = a ? snap(a.t + d.dx / view.current.ppb) - a.t : 0;
    const minT = Math.min(...d.idx.map((i) => notes()[i]?.t ?? 0));
    return { dt: Math.max(-minT, dt), dm: -Math.round(d.dy / view.current.rowH) };
  }
  function resizeDelta(d: Extract<Drag, { kind: 'resize' }>) {
    const a = notes()[d.anchor];
    if (!a) return 0;
    const end = snap(a.t + a.d + d.dx / view.current.ppb);
    return Math.max(minLen(), end - a.t) - a.d;
  }
  const drawLen = (d: Extract<Drag, { kind: 'draw' }>) => Math.max(d.len, snap(d.b0 + d.dx / view.current.ppb) - d.t);
  function velFor(d: Extract<Drag, { kind: 'vel' }>, k: number) {
    const delta = d.v - d.base[d.idx.indexOf(d.anchor)];
    return Math.max(0.02, Math.min(1, d.base[k] + delta));
  }

  /** Clip verlängern, wenn Noten hinter dem Ende liegen */
  function fitClip(p: Project, c: NonNullable<ReturnType<typeof cur>>) {
    const b0 = beatAt(p, c.start), end = Math.max(0, ...(c.notes ?? []).map((n) => n.t + n.d));
    if (secAt(p, b0 + end) > c.start + c.duration + 1e-6) c.duration = secAt(p, barToBeat(p, Math.ceil(beatToBar(p, b0 + end) - 1e-9))) - c.start;
  }
  const edit = (label: string, fn: (ns: Note[], p: Project) => void) => commit(label, (p) => {
    const c = p.clips.find((x) => x.id === clipId);
    if (!c) return;
    c.notes ??= [];
    fn(c.notes, p);
    fitClip(p, c);
  });
  const preview = (m: number, v = 0.8) => { if (track) void engine.preview(track.id, m, v, Math.min(0.6, len * spbAt(P(), cur()?.start ?? 0))); };

  // ---- Zeichnen ----
  useEffect(() => {
    let raf = 0;
    const draw = () => {
      const c = canvas.current, w = wrap.current;
      if (c && w) {
        const dpr = devicePixelRatio || 1, W = w.clientWidth, H = w.clientHeight;
        if (c.width !== W * dpr || c.height !== H * dpr) { c.width = W * dpr; c.height = H * dpr; c.style.width = `${W}px`; c.style.height = `${H}px`; }
        if (!view.current.init && W > 100) initView(W, H);
        const g = c.getContext('2d')!;
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        paint(g, W, H);
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  });

  function initView(W: number, H: number) {
    const v = view.current, ns = notes();
    v.ppb = Math.max(16, Math.min(160, (W - KEYS - 30) / Math.max(4, clipBeats())));
    const rows = Math.floor((H - RULER - VEL) / v.rowH);
    if (ns.length) {
      const lo = Math.min(...ns.map((n) => n.m)), hi = Math.max(...ns.map((n) => n.m));
      v.top = Math.min(127, Math.max(rows - 1, Math.round((lo + hi) / 2 + rows / 2)));
    } else v.top = Math.min(127, 60 + Math.floor(rows / 2) + 6);
    v.b0 = 0; v.init = true;
  }

  function paint(g: CanvasRenderingContext2D, W: number, H: number) {
    const p = P(), c = cur();
    if (!c) return;
    const v = view.current, cb = clipBeats(), laneTop = H - VEL, c0 = beatAt(p, c.start);
    const isBarAt = (b: number) => { const x = beatToBar(p, c0 + b); return Math.abs(x - Math.round(x)) < 1e-6; };
    let key = { tonic: 0, mode: 'major' as 'major' | 'minor' };
    try { key = parseKey(p.key); } catch { /* Standard */ }
    const scale = key.mode === 'minor' ? [0, 2, 3, 5, 7, 8, 10] : [0, 2, 4, 5, 7, 9, 11];
    const inKey = (m: number) => scale.includes((m - key.tonic + 120) % 12);
    g.fillStyle = '#fff'; g.fillRect(0, 0, W, H);

    // Zeilen
    g.save(); g.beginPath(); g.rect(KEYS, RULER, W - KEYS, laneTop - RULER); g.clip();
    for (let m = v.top; Y(m) < laneTop && m >= 0; m--) {
      const y = Y(m);
      g.fillStyle = hover.current === m ? '#eef2fb' : inKey(m) ? '#ffffff' : '#f5f6f9';
      g.fillRect(KEYS, y, W - KEYS, v.rowH);
      g.fillStyle = m % 12 === 0 ? '#dfe3ea' : '#f0f2f5'; g.fillRect(KEYS, y + v.rowH - 1, W - KEYS, 1);
    }
    // nach dem Clip-Ende abgedunkelt
    const xe = X(cb);
    if (xe < W) { g.fillStyle = 'rgba(7,16,38,.045)'; g.fillRect(Math.max(KEYS, xe), RULER, W, laneTop - RULER); }
    // Raster
    const sub = grid && grid * v.ppb >= 6 ? grid : 0;
    for (let b = Math.max(0, Math.floor(B(KEYS))); X(b) < W; b++) {
      g.fillStyle = isBarAt(b) ? '#cfd5df' : '#e3e7ee'; g.fillRect(Math.round(X(b)), RULER, 1, laneTop - RULER);
      if (sub) for (let s = sub; s < 1 - 1e-6; s += sub) { g.fillStyle = '#f0f2f5'; g.fillRect(Math.round(X(b + s)), RULER, 1, laneTop - RULER); }
    }
    // Noten
    const color = track?.color ?? '#003399';
    for (const { n, i } of shown()) {
      const x = X(n.t), w = Math.max(3, n.d * v.ppb - 1), y = Y(n.m) + 1, h = v.rowH - 2;
      if (x > W || x + w < KEYS || y > laneTop || y + h < RULER) continue;
      const on = i >= 0 && sel.current.has(i) || i === -1;
      rr(g, x, y, w, h, 3);
      g.globalAlpha = 0.45 + n.v * 0.55;
      g.fillStyle = on ? '#001f5c' : color; g.fill();
      g.globalAlpha = 1;
      g.setLineDash(n.by.ai ? [3, 2] : []);
      g.strokeStyle = n.by.ai ? '#8f78d6' : on ? '#001f5c' : shade(color); g.lineWidth = 1; g.stroke();
      g.setLineDash([]);
      if (w > 30 && h >= 10) { g.fillStyle = '#fff'; g.font = '600 9.5px "IBM Plex Sans"'; g.fillText(noteName(n.m), x + 4, y + h - 3); }
    }
    // Auswahlrahmen
    const d = drag.current;
    if (d?.kind === 'band') { g.fillStyle = 'rgba(0,51,153,.08)'; g.strokeStyle = '#003399'; const bx = Math.min(d.x0, d.x1), by = Math.min(d.y0, d.y1); g.fillRect(bx, by, Math.abs(d.x1 - d.x0), Math.abs(d.y1 - d.y0)); g.strokeRect(bx + 0.5, by + 0.5, Math.abs(d.x1 - d.x0), Math.abs(d.y1 - d.y0)); }
    // Abspielkopf
    const ph = X(beatAt(p, engine.position) - c0);
    if (ph >= KEYS && ph < W) { g.fillStyle = useStudio.getState().transport.recording ? '#e23e2c' : '#003399'; g.fillRect(Math.round(ph), RULER, 2, laneTop - RULER); }
    g.restore();

    // Velocity-Spur
    g.fillStyle = '#fbfcfe'; g.fillRect(0, laneTop, W, VEL);
    g.fillStyle = '#dfe3ea'; g.fillRect(0, laneTop, W, 1);
    g.font = '600 10.5px "IBM Plex Sans"'; g.fillStyle = '#5a6478'; g.fillText('Anschlag', 8, laneTop + 16);
    g.save(); g.beginPath(); g.rect(KEYS, laneTop + 1, W - KEYS, VEL - 1); g.clip();
    for (let b = Math.max(0, Math.floor(B(KEYS))); X(b) < W; b++) if (isBarAt(b)) { g.fillStyle = '#eceef3'; g.fillRect(Math.round(X(b)), laneTop, 1, VEL); }
    for (const { n, i } of shown()) {
      const x = X(n.t);
      if (x < KEYS - 4 || x > W) continue;
      const on = i >= 0 && sel.current.has(i) || i === -1, hh = n.v * (VEL - 16), y = H - 6 - hh;
      g.fillStyle = on ? '#001f5c' : color;
      g.fillRect(Math.round(x), y, 2, hh);
      g.beginPath(); g.arc(x + 1, y, 3.2, 0, Math.PI * 2); g.fill();
    }
    g.restore();

    // Tastatur
    g.fillStyle = '#fff'; g.fillRect(0, RULER, KEYS, laneTop - RULER);
    g.save(); g.beginPath(); g.rect(0, RULER, KEYS, laneTop - RULER); g.clip();
    for (let m = v.top; Y(m) < laneTop && m >= 0; m--) {
      const y = Y(m), black = BLACK.includes(m % 12);
      g.fillStyle = hover.current === m ? '#c9d5f2' : black ? '#2a3142' : '#fff';
      g.fillRect(black ? 0 : 0, y, black ? KEYS * 0.62 : KEYS, v.rowH);
      g.fillStyle = '#e3e7ee'; g.fillRect(0, y + v.rowH - 1, KEYS, 1);
      if (m % 12 === 0 || hover.current === m) { g.font = `${m % 12 === 0 ? 600 : 500} 9.5px "IBM Plex Mono"`; g.fillStyle = black && hover.current !== m ? '#fff' : '#5a6478'; g.fillText(noteName(m), KEYS - 30, y + v.rowH - 3); }
    }
    g.restore();
    g.fillStyle = '#dfe3ea'; g.fillRect(KEYS - 1, RULER, 1, H - RULER);

    // Lineal mit Takten und Clip-Ende
    g.fillStyle = '#fff'; g.fillRect(0, 0, W, RULER);
    g.fillStyle = '#dfe3ea'; g.fillRect(0, RULER - 1, W, 1);
    g.font = '500 10.5px "IBM Plex Mono"'; g.fillStyle = '#5a6478';
    // Takte des Songs (mit Taktwechseln), Nummern wie im Edit-Fenster
    const barStep = Math.max(1, Math.ceil(40 / (meterAt(p, c.start).barLen * v.ppb)));
    for (let bar = Math.max(0, Math.floor(beatToBar(p, c0 + Math.max(0, B(KEYS))))); ; bar++) {
      const x = X(barToBeat(p, bar) - c0);
      if (x >= W) break;
      if (x < KEYS) continue;
      if (bar % barStep === 0) { g.fillText(String(bar + 1), x + 4, 16); g.fillRect(Math.round(x), RULER - 7, 1, 6); }
    }
    const xEnd = X(cb + (d?.kind === 'end' ? endDelta(d) : 0));
    if (xEnd >= KEYS && xEnd <= W + 8) {
      g.fillStyle = '#003399'; g.beginPath(); g.moveTo(xEnd, 3); g.lineTo(xEnd, RULER - 2); g.lineTo(xEnd - 9, RULER - 2); g.closePath(); g.fill();
    }
    g.fillStyle = '#fff'; g.fillRect(0, 0, KEYS - 1, RULER - 1);
    g.font = '600 10px "IBM Plex Sans"'; g.fillStyle = '#8a93a6'; g.fillText('Takt', 8, 16);
  }
  const endDelta = (d: Extract<Drag, { kind: 'end' }>) => {
    const bpb = meterAt(P(), cur()?.start ?? 0).barLen;
    const target = Math.max(1, Math.round((d.beats0 + d.dx / view.current.ppb) / bpb)) * bpb;
    return target - d.beats0;
  };

  // ---- Treffer ----
  const local = (e: { clientX: number; clientY: number }) => { const r = wrap.current!.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  function hitNote(x: number, y: number): { i: number; edge: boolean } | null {
    const ns = notes(), m = M(y);
    for (let i = ns.length - 1; i >= 0; i--) {
      const n = ns[i];
      if (n.m !== m) continue;
      const x0 = X(n.t), x1 = X(n.t + n.d);
      if (x >= x0 && x <= Math.max(x1, x0 + 4)) return { i, edge: x > x1 - Math.min(7, (x1 - x0) / 3) };
    }
    return null;
  }
  function hitVel(x: number): number | null {
    const ns = notes();
    let best: number | null = null, dist = 7;
    ns.forEach((n, i) => { const dx = Math.abs(X(n.t) - x); if (dx < dist || (dx <= dist && sel.current.has(i))) { dist = dx; best = i; } });
    return best;
  }

  // ---- Maus ----
  const onDown = (e: React.PointerEvent) => {
    if (e.button === 2) return;
    active.current = true;
    const { x, y } = local(e), H = wrap.current!.clientHeight, laneTop = H - VEL;
    (e.target as Element).setPointerCapture(e.pointerId);
    if (y < RULER) {
      if (Math.abs(x - X(clipBeats())) < 10) { drag.current = { kind: 'end', x0: e.clientX, dx: 0, beats0: clipBeats() }; return; }
      if (x > KEYS) engine.locate(Math.max(0, secAt(P(), cb0() + B(x))));
      return;
    }
    if (x < KEYS) { if (y < laneTop) preview(M(y)); return; }
    if (y >= laneTop) {
      const i = hitVel(x);
      if (i === null) return;
      const idx = sel.current.has(i) ? [...sel.current] : [i];
      drag.current = { kind: 'vel', idx, anchor: i, base: idx.map((k) => notes()[k].v), v: velOfY(y, H) };
      rerender(); return;
    }
    const h = hitNote(x, y);
    if (h) {
      if (e.shiftKey) { if (sel.current.has(h.i)) sel.current.delete(h.i); else sel.current.add(h.i); rerender(); return; }
      if (!sel.current.has(h.i)) sel.current = new Set([h.i]);
      const idx = [...sel.current];
      if (h.edge) drag.current = { kind: 'resize', idx, anchor: h.i, x0: e.clientX, dx: 0 };
      else { drag.current = { kind: 'move', idx, anchor: h.i, x0: e.clientX, y0: e.clientY, dx: 0, dy: 0, copy: e.altKey, lastPitch: notes()[h.i].m }; preview(notes()[h.i].m, notes()[h.i].v); }
      rerender(); return;
    }
    if (e.shiftKey) { drag.current = { kind: 'band', x0: x, y0: y, x1: x, y1: y, add: [...sel.current] }; return; }
    // Neue Note: rastet am Anfang der Rasterzelle ein, Ziehen nach rechts verlängert
    sel.current = new Set();
    const m = clampM(M(y)), t = Math.max(0, floorSnap(B(x)));
    drag.current = { kind: 'draw', t, b0: B(x), m, len, x0: e.clientX, dx: 0 };
    preview(m);
    rerender();
  };

  const velOfY = (y: number, H: number) => Math.max(0.02, Math.min(1, (H - 6 - y) / (VEL - 16)));

  const onMove = (e: React.PointerEvent) => {
    const d = drag.current, { x, y } = local(e);
    if (!d) {
      const H = wrap.current!.clientHeight;
      const m = y > RULER && y < H - VEL ? M(y) : null;
      if (m !== hover.current) hover.current = m;
      const h = x > KEYS && y > RULER && y < H - VEL ? hitNote(x, y) : null;
      wrap.current!.style.cursor = y < RULER ? (Math.abs(x - X(clipBeats())) < 10 ? 'ew-resize' : 'pointer') : x < KEYS ? 'pointer' : y >= H - VEL ? 'ns-resize' : h?.edge ? 'ew-resize' : h ? (e.altKey ? 'copy' : 'grab') : 'crosshair';
      return;
    }
    if (d.kind === 'move') {
      d.dx = e.clientX - d.x0; d.dy = e.clientY - d.y0; d.copy = e.altKey;
      const a = notes()[d.anchor];
      if (a) { const nm = clampM(a.m - Math.round(d.dy / view.current.rowH)); if (nm !== d.lastPitch) { d.lastPitch = nm; preview(nm, a.v); } }
    } else if (d.kind === 'resize' || d.kind === 'draw' || d.kind === 'end') d.dx = e.clientX - d.x0;
    else if (d.kind === 'band') { d.x1 = x; d.y1 = y; bandSelect(d); }
    else if (d.kind === 'vel') d.v = velOfY(y, wrap.current!.clientHeight);
  };

  function bandSelect(d: Extract<Drag, { kind: 'band' }>) {
    const b0 = B(Math.min(d.x0, d.x1)), b1 = B(Math.max(d.x0, d.x1)), mHi = M(Math.min(d.y0, d.y1)), mLo = M(Math.max(d.y0, d.y1));
    const s = new Set(d.add);
    notes().forEach((n, i) => { if (n.m >= mLo && n.m <= mHi && n.t < b1 && n.t + n.d > b0) s.add(i); });
    sel.current = s;
  }

  const onUp = () => {
    const d = drag.current; drag.current = null;
    if (!d) return;
    const at = new Date().toISOString(), by = { userId: user.id, at };
    if (d.kind === 'draw') {
      const n: Note = { t: r3(d.t), d: r3(drawLen(d)), m: d.m, v: 0.8, by };
      const i = notes().length;
      edit('Note setzen', (ns) => { ns.push(n); });
      sel.current = new Set([i]);
      if (d.dx > 8) setLen(n.d);
    } else if (d.kind === 'move') {
      const { dt, dm } = moveDelta(d);
      if (!dt && !dm && !d.copy) { rerender(); return; }
      if (d.copy) {
        const start = notes().length;
        edit(d.idx.length > 1 ? 'Noten kopieren' : 'Note kopieren', (ns) => { for (const i of d.idx) ns.push({ ...ns[i], t: r3(Math.max(0, ns[i].t + dt)), m: clampM(ns[i].m + dm), by }); });
        sel.current = new Set(d.idx.map((_, k) => start + k));
      } else edit(d.idx.length > 1 ? 'Noten verschieben' : 'Note verschieben', (ns) => { for (const i of d.idx) { ns[i].t = r3(Math.max(0, ns[i].t + dt)); ns[i].m = clampM(ns[i].m + dm); ns[i].by = by; } });
    } else if (d.kind === 'resize') {
      const dd = resizeDelta(d);
      if (dd) { edit('Notenlänge', (ns) => { for (const i of d.idx) { ns[i].d = r3(Math.max(minLen(), ns[i].d + dd)); ns[i].by = by; } }); const a = notes()[d.anchor]; if (a) setLen(a.d); }
    } else if (d.kind === 'vel') {
      edit('Anschlag', (ns) => { d.idx.forEach((i, k) => { if (ns[i]) ns[i].v = Math.round(velFor(d, k) * 100) / 100; }); });
    } else if (d.kind === 'end') {
      const dd = endDelta(d);
      if (dd) commit('Clip-Länge', (p) => { const c = p.clips.find((x) => x.id === clipId); if (c) c.duration = secAt(p, beatAt(p, c.start) + d.beats0 + dd) - c.start; });
    }
    rerender();
  };

  const deleteSelected = () => {
    if (!sel.current.size) return;
    const del = new Set(sel.current);
    edit(del.size > 1 ? 'Noten löschen' : 'Note löschen', (ns) => { for (let i = ns.length - 1; i >= 0; i--) if (del.has(i)) ns.splice(i, 1); });
    sel.current = new Set(); rerender();
  };
  const deleteAt = (e: React.MouseEvent) => {
    const { x, y } = local(e);
    if (x < KEYS || y < RULER || y > wrap.current!.clientHeight - VEL) return false;
    const h = hitNote(x, y);
    if (!h) return false;
    edit('Note löschen', (ns) => { ns.splice(h.i, 1); });
    sel.current = new Set(); rerender();
    return true;
  };

  const quantize = () => {
    const g = grid || 0.25;
    const idx = sel.current.size ? [...sel.current] : notes().map((_, i) => i);
    if (!idx.length) return toast('Keine Noten zum Quantisieren');
    edit('Quantisieren', (ns) => { for (const i of idx) { ns[i].t = r3(Math.max(0, Math.round(ns[i].t / g) * g)); ns[i].d = r3(Math.max(g, Math.round(ns[i].d / g) * g)); } });
    toast(`${idx.length} ${idx.length === 1 ? 'Note' : 'Noten'} auf ${GRIDS.find((x) => x.beats === g)?.label ?? '1/16'} quantisiert`);
  };

  // ---- Tastatur (nur wenn der Editor zuletzt angeklickt wurde) ----
  useEffect(() => {
    const onPointer = (e: PointerEvent) => { active.current = !!wrap.current?.closest('.panel')?.contains(e.target as Node); pianoFocus.active = active.current; };
    const onKey = (e: KeyboardEvent) => {
      if (!active.current || (e.target as HTMLElement).closest('input, textarea, select')) return;
      const mod = e.metaKey || e.ctrlKey, ns = notes(), stop = () => { e.preventDefault(); e.stopImmediatePropagation(); };
      const idx = [...sel.current].filter((i) => i < ns.length);
      if (e.key === 'Delete' || e.key === 'Backspace') { stop(); deleteSelected(); }
      else if (e.key === 'Escape') { stop(); if (sel.current.size) { sel.current = new Set(); rerender(); } else onClose(); }
      else if (mod && e.key.toLowerCase() === 'a') { stop(); sel.current = new Set(ns.map((_, i) => i)); rerender(); }
      else if (mod && e.key.toLowerCase() === 'd' && idx.length) {
        stop();
        const s0 = Math.min(...idx.map((i) => ns[i].t)), s1 = Math.max(...idx.map((i) => ns[i].t + ns[i].d));
        const span = Math.max(grid || 1, Math.ceil((s1 - s0) / (grid || 1) - 1e-9) * (grid || 1)), start = ns.length;
        edit('Noten duplizieren', (n) => { for (const i of idx) n.push({ ...n[i], t: r3(n[i].t + span), by: { userId: user.id, at: new Date().toISOString() } }); });
        sel.current = new Set(idx.map((_, k) => start + k)); rerender();
      } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && idx.length) {
        stop();
        const dm = (e.key === 'ArrowUp' ? 1 : -1) * (e.shiftKey ? 12 : 1);
        edit('Transponieren', (n) => { for (const i of idx) n[i].m = clampM(n[i].m + dm); });
        preview(clampM(ns[idx[0]].m + dm));
      } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && idx.length) {
        stop();
        const step = (e.key === 'ArrowRight' ? 1 : -1) * (grid || 0.25), minT = Math.min(...idx.map((i) => ns[i].t));
        const dt = Math.max(-minT, step);
        edit('Noten verschieben', (n) => { for (const i of idx) n[i].t = r3(n[i].t + dt); });
      } else if (!mod && e.key.toLowerCase() === 'q') { stop(); quantize(); }
      else if (!mod && e.key.toLowerCase() === 's') stop(); // im Editor nicht den Clip teilen
    };
    window.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('pointerdown', onPointer, true); window.removeEventListener('keydown', onKey, true); pianoFocus.active = false; };
  });

  const onWheel = (e: React.WheelEvent) => {
    const v = view.current, H = wrap.current?.clientHeight ?? 300;
    if (e.ctrlKey || e.metaKey) {
      const { x } = local(e), b = B(x);
      v.ppb = Math.max(10, Math.min(400, v.ppb * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
      v.b0 = Math.max(0, b - (x - KEYS) / v.ppb);
    } else if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      v.b0 = Math.max(0, Math.min(clipBeats() + 32, v.b0 + (e.deltaX || e.deltaY) / v.ppb));
    } else {
      const rows = Math.floor((H - RULER - VEL) / v.rowH);
      const dRows = Math.round(e.deltaY / v.rowH / 1.5) || Math.sign(e.deltaY);
      v.top = Math.max(rows - 1, Math.min(127, v.top - dRows));
    }
  };
  const zoomBy = (f: number) => { const v = view.current; v.ppb = Math.max(10, Math.min(400, v.ppb * f)); };
  const zoomRows = (d: number) => { const v = view.current; v.rowH = Math.max(8, Math.min(24, v.rowH + d)); };

  if (!clip || !track) return null;
  const count = clip.notes?.length ?? 0;
  const sound = SOUNDS.find((s) => s.plugin === track.instrument?.plugin && (s.preset === undefined || s.preset === (track.instrument?.preset ?? 'keys')));

  return (
    <Panel className="pr-panel" title={<>MIDI-Editor <span className="t2" style={{ fontFamily: 'var(--font)', fontWeight: 400, fontSize: 13 }}>{clip.name} · {track.name} · {count} {count === 1 ? 'Note' : 'Noten'}</span></>} right={<>
      <label className="prl">Raster
        <select className="input" value={grid} onChange={(e) => setGrid(Number(e.target.value))}>{GRIDS.map((g) => <option key={g.label} value={g.beats}>{g.label}</option>)}</select>
      </label>
      <label className="prl">Länge
        <select className="input" value={LENGTHS.some((l) => Math.abs(l.beats - len) < 1e-6) ? len : ''} onChange={(e) => setLen(Number(e.target.value))}>
          {!LENGTHS.some((l) => Math.abs(l.beats - len) < 1e-6) && <option value="">{Math.round(len * 1000) / 1000} Schläge</option>}
          {LENGTHS.map((l) => <option key={l.label} value={l.beats}>{l.label}</option>)}
        </select>
      </label>
      <label className="prl">Klang
        <select className="input" value={sound?.label ?? ''} onChange={(e) => { const s = SOUNDS.find((x) => x.label === e.target.value)!; commit('Klang', (p) => { const t = p.tracks.find((x) => x.id === track.id); if (t) t.instrument = { plugin: s.plugin, preset: s.preset }; }); }}>
          {!sound && <option value="">{track.instrument?.plugin.startsWith('bridge:') ? `Plugin ${track.instrument.plugin.slice(7)}` : 'Eigenes'}</option>}
          {SOUNDS.map((s) => <option key={s.label}>{s.label}</option>)}
        </select>
      </label>
      <button className="btn small" onClick={quantize} title="Taste Q. Auswahl oder alle Noten aufs Raster">Quantisieren</button>
      <div className="seg">
        <button className="btn small" title="Kleiner" onClick={() => zoomBy(1 / 1.25)}>−</button>
        <button className="btn small" title="Größer" onClick={() => zoomBy(1.25)}>+</button>
        <button className="btn small" title="Zeilen niedriger" onClick={() => zoomRows(-2)}>↕−</button>
        <button className="btn small" title="Zeilen höher" onClick={() => zoomRows(2)}>↕+</button>
      </div>
      <button className="btn small" onClick={onClose} title="Esc">Schließen</button>
    </>}>
      <div className="pr" ref={wrap} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={() => { if (!drag.current) hover.current = null; }} onWheel={onWheel}
        onDoubleClick={(e) => { deleteAt(e); }} onContextMenu={(e) => { e.preventDefault(); deleteAt(e); }}>
        <canvas ref={canvas} />
      </div>
      <div className="pr-hint">
        Klick setzt eine Note, nach rechts ziehen verlängert · Note ziehen verschiebt, am rechten Rand Länge ändern · ⌥ beim Ziehen kopiert · Doppelklick oder Rechtsklick löscht · ⇧ Ziehen wählt mehrere · ↑ ↓ transponieren (⇧ Oktave) · ⌘D duplizieren · Q quantisieren · Tastatur links zum Vorhören
      </div>
    </Panel>
  );
}

function rr(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  r = Math.max(0, Math.min(r, w / 2, h / 2)); g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r); g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
}
function shade(hex: string) { const n = parseInt(hex.slice(1), 16); return `rgb(${Math.round((n >> 16) * 0.7)},${Math.round(((n >> 8) & 255) * 0.7)},${Math.round((n & 255) * 0.7)})`; }
